/**
 * AI INTELLIGENCE (Intelligence). Operational AI metrics across customers from
 * records the platform already keeps: reliability, usage, latency, tool-call
 * outcomes, evaluation and Phoebe sessions. Counts only, never a prompt, answer
 * or tool argument. What is not captured is listed as such, never shown as 0.
 */
import { useEffect, useState } from 'react';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import {
  AI_WINDOWS,
  INSTRUMENTATION_TEXT,
  aiRequests,
  count,
  modelCost,
  ms,
  pct,
  runRates,
  type AiOverviewRead,
  type AiWindow,
} from '../platform/ai.js';

type Tone = 'good' | 'look' | 'urgent' | 'unsure' | 'sky';
const ROUTES = ['deterministic', 'jev', 'reasoning', 'human'] as const;
const ACTION_TONE: Record<string, Tone> = {
  EXECUTED: 'good',
  SKIPPED_DUPLICATE: 'sky',
  REFUSED: 'look',
  FAILED: 'urgent',
  IN_FLIGHT: 'unsure',
  OUTCOME_UNKNOWN: 'urgent',
  NOT_EXECUTED: 'unsure',
};
const STATE_TEXT: Record<string, string> = {
  not_captured: 'not captured',
  logs_only: 'Logs & Errors only (source: AI and agents)',
};

function Card(props: { label: string; value: string; tone?: Tone; note: string }) {
  return (
    <div className="metric">
      <span className="metric-label">{props.label}</span>
      {props.tone ? (
        <span className="pill" data-tone={props.tone}>
          {props.value}
        </span>
      ) : (
        <span className="metric-value">{props.value}</span>
      )}
      <span className="metric-note">{props.note}</span>
    </div>
  );
}

const rateTone = (r: number | null, look: number, urgent: number): Tone =>
  r === null ? 'unsure' : r >= urgent ? 'urgent' : r >= look ? 'look' : 'good';

/** Pure: everything the screen shows, from one read. Rendered directly by tests. */
export function AiOverviewView({ read }: { read: AiOverviewRead }) {
  const p = read.platform;
  const rates = runRates(p);
  const d = p.decisions;
  const unreadable = read.organizations.filter((o) => o.state === 'unreadable').length;
  const gaps = Object.entries(read.instrumentation).filter(([, v]) => v !== 'measured');
  return (
    <>
      {unreadable > 0 ? (
        <div className="banner" role="alert">
          <strong>
            {unreadable} customer{unreadable === 1 ? '' : 's'} could not be read.
          </strong>{' '}
          Platform totals below exclude them.
        </div>
      ) : null}

      <h3>Reliability</h3>
      <div className="metrics">
        <Card
          label="Agent runs"
          value={count(p.runs.total)}
          note={`${count(rates.finished)} finished in the window`}
        />
        <Card
          label="Failure rate"
          value={pct(rates.failed)}
          tone={rateTone(rates.failed, 0.05, 0.2)}
          note="FAILED of finished runs"
        />
        <Card
          label="Blocked rate"
          value={pct(rates.blocked)}
          tone={rateTone(rates.blocked, 0.05, 0.2)}
          note="policy-blocked or budget-exhausted of finished runs"
        />
        <Card
          label="Run errors"
          value={count(p.runs.timelineErrors)}
          tone={p.runs.timelineErrors > 0 ? 'look' : 'good'}
          note="error events on run timelines"
        />
      </div>

      <h3>Usage and cost</h3>
      <div className="metrics">
        <Card
          label="Agent tokens"
          value={`${count(p.usage.inputTokens)} in`}
          note={`${count(p.usage.outputTokens)} out · ${count(p.usage.records)} usage records`}
        />
        <Card
          label="Agent model cost"
          value={modelCost(p.usage)}
          tone={p.usage.modelRecordsWithoutCost > 0 ? 'unsure' : undefined}
          note={`${count(p.usage.modelRecords)} model calls · a call without a recorded cost is unknown, not $0`}
        />
        <Card
          label="Calls and retries"
          value={`${count(p.usage.toolCalls)} tool calls`}
          note={`${count(p.usage.connectorCalls)} connector calls · ${count(p.usage.retries)} retries`}
        />
        <Card
          label="Decision cost"
          value={`$${d.totalCostUsd.toFixed(2)}`}
          note={`cost per confirmed decision ${
            d.costPerSuccessfulUsd === null ? '—' : `$${d.costPerSuccessfulUsd.toFixed(4)}`
          }`}
        />
      </div>

      <h3>Latency</h3>
      <div className="metrics">
        <Card
          label="Agent run time"
          value={`p50 ${ms(p.runs.runtimeMs.p50)}`}
          note={`p95 ${ms(p.runs.runtimeMs.p95)} · active runtime of finished runs`}
        />
        {ROUTES.map((r) => (
          <Card
            key={r}
            label={`Decision · ${r}`}
            value={`p50 ${ms(d.latencyByRoute[r].p50)}`}
            note={`p95 ${ms(d.latencyByRoute[r].p95)} · ${pct(d.routeShare[r])} of decisions`}
          />
        ))}
      </div>

      <h3>Tool-call outcomes</h3>
      {p.actions.total === 0 ? (
        <p className="quiet">No agent actions in this window (read successfully).</p>
      ) : (
        <table className="stack">
          <thead>
            <tr>
              <th>Result</th>
              <th>Actions</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(p.actions.byResult).map(([k, n]) => (
              <tr key={k}>
                <td data-label="Result">
                  <span className="pill" data-tone={ACTION_TONE[k] ?? 'unsure'}>
                    {k.toLowerCase().replace(/_/g, ' ')}
                  </span>
                </td>
                <td data-label="Actions">{count(n)}</td>
                <td data-label="Share">{pct(n / p.actions.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Evaluation</h3>
      <div className="metrics">
        <Card
          label="Decisions"
          value={count(d.n)}
          note={`${count(d.judged)} with a confirmed, overridden or reverted outcome`}
        />
        <Card
          label="Human override rate"
          value={pct(d.humanOverrideRate)}
          tone={rateTone(d.humanOverrideRate, 0.1, 0.25)}
          note="of judged decisions; — means none judged yet"
        />
        <Card
          label="Resolved without reasoning"
          value={pct(d.resolvedWithoutReasoning)}
          note={`Jev escalation ${pct(d.jevEscalationRate)}`}
        />
        {ROUTES.map((r) => (
          <Card
            key={r}
            label={`Accuracy · ${r}`}
            value={pct(d.accuracyByRoute[r].accuracy)}
            tone={rateTone(
              d.accuracyByRoute[r].accuracy === null ? null : 1 - d.accuracyByRoute[r].accuracy!,
              0.1,
              0.25,
            )}
            note={`${count(d.accuracyByRoute[r].judged)} judged`}
          />
        ))}
      </div>

      <h3>Phoebe (virtual workforce)</h3>
      <div className="metrics">
        <Card label="Cases" value={count(p.phoebe.cases)} note="intake cases opened" />
        <Card
          label="Sessions"
          value={count(Object.values(p.phoebe.sessions).reduce((a, b) => a + b, 0))}
          note={Object.entries(p.phoebe.sessions)
            .map(([k, n]) => `${k} ${n}`)
            .join(' · ')}
        />
        <Card
          label="Turns"
          value={count(p.phoebe.turns)}
          note={`ended: ${
            Object.entries(p.phoebe.endReasons)
              .filter(([, n]) => n > 0)
              .map(([k, n]) => `${k.replace(/_/g, ' ')} ${n}`)
              .join(' · ') || 'none'
          }`}
        />
        <Card
          label="Phoebe tokens"
          value="not captured"
          tone="unsure"
          note="Phoebe token usage is not persisted anywhere"
        />
      </div>

      <h3>By customer</h3>
      <table className="stack">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Runs</th>
            <th>Failure</th>
            <th>Tokens in / out</th>
            <th>Model cost</th>
            <th>Decisions</th>
            <th>Phoebe sessions</th>
          </tr>
        </thead>
        <tbody>
          {read.organizations.map((o) =>
            o.state === 'unreadable' ? (
              <tr key={o.id}>
                <td data-label="Customer">{o.name}</td>
                <td colSpan={6}>
                  <span className="pill" data-tone="urgent">
                    could not be read
                  </span>
                </td>
              </tr>
            ) : (
              <tr key={o.id}>
                <td data-label="Customer">{o.name}</td>
                <td data-label="Runs">{count(o.runs.total)}</td>
                <td data-label="Failure">{pct(runRates(o).failed)}</td>
                <td data-label="Tokens in / out">
                  {count(o.usage.inputTokens)} / {count(o.usage.outputTokens)}
                </td>
                <td data-label="Model cost">{modelCost(o.usage)}</td>
                <td data-label="Decisions">{count(o.decisions.n)}</td>
                <td data-label="Phoebe sessions">
                  {count(Object.values(o.phoebe.sessions).reduce((a, b) => a + b, 0))}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      <h3>Not captured here</h3>
      <ul>
        {gaps.map(([k, v]) => (
          <li key={k}>
            <strong>{INSTRUMENTATION_TEXT[k] ?? k}</strong>:{' '}
            <span className="pill" data-tone={v === 'not_captured' ? 'unsure' : 'sky'}>
              {STATE_TEXT[v] ?? v}
            </span>
          </li>
        ))}
      </ul>
      <p className="quiet">
        Window: since {new Date(read.since).toLocaleString()} · measured{' '}
        {new Date(read.measuredAt).toLocaleString()}. Counts come from each customer&apos;s own
        records, one customer at a time; no prompt, answer, tool argument or clinical text leaves
        the customer.
      </p>
    </>
  );
}

export function AiIntelligence({ actor, client }: { actor: PlatformActor; client: ConsoleClient }) {
  const [windowDays, setWindowDays] = useState<AiWindow>(7);
  const [read, setRead] = useState<ConsoleOutcome<AiOverviewRead> | null>(null);
  useEffect(() => {
    setRead(null);
    void client<AiOverviewRead>(aiRequests.overview(windowDays), actor).then(setRead);
  }, [actor, client, windowDays]);

  return (
    <section>
      <div className="filters">
        <div className="field">
          <label htmlFor="aiWindow">Window</label>
          <select
            id="aiWindow"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value) as AiWindow)}
          >
            {AI_WINDOWS.map((w) => (
              <option key={w} value={w}>
                last {w} days
              </option>
            ))}
          </select>
        </div>
      </div>
      {read === null ? (
        <p className="quiet">Reading every customer&apos;s AI counts…</p>
      ) : read.kind !== 'ok' ? (
        <div className="banner" role="alert">
          <strong>AI Intelligence is not available to you.</strong> {read.message}
        </div>
      ) : (
        <AiOverviewView read={read.value} />
      )}
    </section>
  );
}
